import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, PlanLimits } from '@/types';

type Plan = 'free' | 'pro' | 'team' | 'enterprise';

// Plan limits for each tier (matching backend)
const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  free: {
    maxAgents: 1,
    maxSessionDuration: 60,
    maxConcurrentSessions: 1,
    features: ['basic_terminal'],
  },
  pro: {
    maxAgents: 5,
    maxSessionDuration: 1440,
    maxConcurrentSessions: 3,
    features: ['basic_terminal', 'custom_subdomain', 'session_recording'],
  },
  team: {
    maxAgents: 20,
    maxSessionDuration: 1440,
    maxConcurrentSessions: 10,
    features: ['basic_terminal', 'custom_subdomain', 'session_recording', 'team_management'],
  },
  enterprise: {
    maxAgents: 100,
    maxSessionDuration: 0, // unlimited
    maxConcurrentSessions: 50,
    features: ['basic_terminal', 'custom_subdomain', 'session_recording', 'team_management', 'sso', 'audit_logs'],
  },
};

interface DebugState {
  // Debug mode state
  isDebugMode: boolean;
  viewAsPlan: Plan | null;
  
  // Actions
  enableDebugMode: () => void;
  disableDebugMode: () => void;
  setViewAsPlan: (plan: Plan | null) => void;
  
  // Helpers
  getEffectivePlan: (actualPlan: Plan) => Plan;
  getEffectivePlanLimits: (actualPlan: Plan) => PlanLimits;
  canDebug: (user: User | null) => boolean;
}

export const useDebugStore = create<DebugState>()(
  persist(
    (set, get) => ({
      // Initial state
      isDebugMode: false,
      viewAsPlan: null,
      
      // Actions
      enableDebugMode: () => set({ isDebugMode: true }),
      
      disableDebugMode: () => set({ isDebugMode: false, viewAsPlan: null }),
      
      setViewAsPlan: (plan) => set({ viewAsPlan: plan }),
      
      // Helpers
      getEffectivePlan: (actualPlan) => {
        const { isDebugMode, viewAsPlan } = get();
        if (isDebugMode && viewAsPlan) {
          return viewAsPlan;
        }
        return actualPlan;
      },
      
      getEffectivePlanLimits: (actualPlan) => {
        const effectivePlan = get().getEffectivePlan(actualPlan);
        return PLAN_LIMITS[effectivePlan];
      },
      
      canDebug: (user) => {
        if (!user) return false;
        return user.email.endsWith('@whss.dev');
      },
    }),
    {
      name: 'whss-debug',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        isDebugMode: state.isDebugMode,
        viewAsPlan: state.viewAsPlan,
      }),
    }
  )
);

// Export plan limits for use elsewhere
export { PLAN_LIMITS };
export type { Plan };
